import { fmtAge } from "@kmitl/shared";
import type { FloodArea, FloodAreaFeed } from "@kmitl/shared";
import { PanelHeader } from "./PanelHeader";

interface Props {
  feed: FloodAreaFeed | null;
  ageMinutes?: number | null;
  fallbackTier?: "live" | "unavailable" | "scenario" | "cache" | "database" | "reference";
  loading?: boolean;
}

/**
 * Tier is the load-bearing word on this panel, so it is spelled out rather than
 * encoded as a colour. The three values are not a severity ramp — they say HOW
 * we know:
 *   confirmed  a named place in coverage AND sensor evidence agree
 *   reported   a named place in coverage, no sensor to back it
 *   sensor     sensor evidence only, nothing in coverage names the place
 */
const TIER_COPY: Record<FloodArea["tier"], { en: string; th: string }> = {
  confirmed: { en: "Confirmed", th: "ยืนยันแล้ว" },
  reported: { en: "Reported", th: "มีรายงาน" },
  sensor: { en: "Sensor only", th: "เฉพาะเครื่องวัด" },
};

const TIER_ORDER: Record<FloodArea["tier"], number> = { confirmed: 0, reported: 1, sensor: 2 };

function ago(iso: string | null): string {
  if (!iso) return "";
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (!Number.isFinite(mins)) return "";
  if (mins < 60) return `${mins}m`;
  const h = Math.round(mins / 60);
  if (h < 48) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

function AreaName({ area }: { area: FloodArea }) {
  // A sensor-tier point legitimately has no place name — the source reports a
  // province, not a street. Printing an invented label here would be the same
  // error as the deleted flood-prone polygons.
  if (!area.areaEn && !area.areaTh) {
    return (
      <span className="fab-name fab-name--none">
        {area.provinceEn || area.provinceTh || "Unnamed area"}
        <span className="fab-name__scope"> · no place named in coverage</span>
      </span>
    );
  }
  return (
    <span className="fab-name">
      <span lang="th">{area.areaTh}</span>
      {area.areaEn && area.areaEn !== area.areaTh ? <span className="fab-name__en">{area.areaEn}</span> : null}
    </span>
  );
}

function TierBadge({ tier }: { tier: FloodArea["tier"] }) {
  return (
    <span className={`fab-tier fab-tier--${tier}`} title={`${TIER_COPY[tier].en} · ${TIER_COPY[tier].th}`}>
      {TIER_COPY[tier].en}
    </span>
  );
}

function ScoreBar({ score, band }: { score: number | null; band: string | null }) {
  if (score == null) return null;
  return (
    <div className="fab-score" title={`FloodDash watch score ${score}/100 · band ${band ?? "—"}`}>
      <span className="fab-score__num mono">{score}</span>
      <span className="fab-score__track">
        <span className={`fab-score__fill fab-score__fill--${band ?? "unknown"}`} style={{ width: `${Math.max(2, Math.min(100, score))}%` }} />
      </span>
      <span className="fab-score__band mono">{band ?? "—"}</span>
    </div>
  );
}

export function FloodAreaBoard({ feed, ageMinutes, fallbackTier, loading }: Props) {
  if (loading && !feed) {
    return (
      <section className="panel flood-area-board">
        <PanelHeader title="Flood by area" source="flooddash" />
        <div className="panel-body">
          <p className="fab-note">Reading FloodDash area intelligence…</p>
        </div>
      </section>
    );
  }

  if (!feed || feed.areas.length === 0) {
    return (
      <section className="panel flood-area-board">
        <PanelHeader title="Flood by area" ageMinutes={ageMinutes} fallbackTier={fallbackTier} source="flooddash" />
        <div className="panel-body">
          {/* No areas is not "no flooding". Saying so would be an all-clear
              the source never issued. */}
          <p className="fab-note">
            No area intelligence published. This is <strong>not</strong> an all-clear — FloodDash has not
            reported anything in this window.
          </p>
        </div>
      </section>
    );
  }

  // The campus area first: it is the one an operator on site actually needs.
  // Rest by tier then distance so confirmed/reported areas stay visible above
  // sensor-only ones, which are the least specific.
  const [campusArea, ...rest] = [...feed.areas].sort(
    (a, b) => TIER_ORDER[a.tier] - TIER_ORDER[b.tier] || a.distanceKm - b.distanceKm,
  );

  return (
    <section className="panel flood-area-board">
      <PanelHeader
        title="Flood by area"
        ageMinutes={ageMinutes}
        fallbackTier={fallbackTier}
        source="flooddash"
        actions={
          feed.meta.windowHours ? (
            <span className="fab-window mono" title="Reporting look-back window">
              {feed.meta.windowHours}H WINDOW
            </span>
          ) : undefined
        }
      />

      <div className="panel-body">
        {/* ── The campus area ─────────────────────────────────────────── */}
        <article className="fab-focus" lang="en">
          <header className="fab-focus__head">
            <AreaName area={campusArea} />
            <TierBadge tier={campusArea.tier} />
          </header>

          <div className="fab-focus__meta mono">
            <span>{campusArea.distanceKm} KM FROM CAMPUS</span>
            <span aria-hidden>·</span>
            <span>{campusArea.provinceEn}</span>
            {campusArea.placeKind && (
              <>
                <span aria-hidden>·</span>
                <span>{campusArea.placeKind}</span>
              </>
            )}
          </div>

          <ScoreBar score={campusArea.score} band={campusArea.band} />

          {/* Corroboration scope — the honesty line. "4 sources" means
              something very different when three of them name this district
              than when all four name the province. */}
          <p className={`fab-scope fab-scope--${campusArea.corroborationScope}`}>
            {campusArea.corroborationScope === "area" && (
              <>All {campusArea.sourceCount} sources name this area directly.</>
            )}
            {campusArea.corroborationScope === "province" && (
              <>
                <strong>{campusArea.news.filter((n) => n.here).length} of {campusArea.sourceCount}</strong>{" "}
                sources name this area; the rest cover {campusArea.provinceEn} more broadly.
              </>
            )}
            {campusArea.corroborationScope === "none" && (
              <>No coverage names this area — sensor evidence only.</>
            )}
          </p>

          {/* Impacts, each carrying the headline it was read from. */}
          {campusArea.impacts.length > 0 && (
            <ul className="fab-impacts">
              {campusArea.impacts.map((im, i) => (
                <li key={`${im.en}-${i}`} className="fab-impact">
                  <span className="fab-impact__label" lang="th">{im.th}</span>
                  {im.source?.link && (
                    <a className="fab-impact__src" href={im.source.link} target="_blank" rel="noopener noreferrer">
                      {im.source.publisher ?? im.source.title}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* Sensor facts, quoted from the source rather than paraphrased. */}
          {campusArea.systemEvidence.length > 0 && (
            <ul className="fab-evidence">
              {campusArea.systemEvidence.map((e, i) => (
                <li key={i}>
                  <span className="fab-evidence__k" lang="th">{e.th}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Depth: a sourced figure or nothing. Most headlines state no
              depth at all, and a rendered range would be two invented
              numbers wearing a unit. */}
          {campusArea.depthCm != null ? (
            <p className="fab-depth">
              <strong className="mono">{campusArea.depthCm} cm</strong>
              {campusArea.depthSource && <span> — {campusArea.depthSource}</span>}
            </p>
          ) : (
            <p className="fab-depth fab-depth--none">
              No source states a water depth here. Not estimated.
            </p>
          )}
        </article>

        {/* ── The coverage behind it ──────────────────────────────────── */}
        {campusArea.news.length > 0 && (
          <div className="fab-news">
            <h4 className="fab-sub eyebrow mono">
              Coverage · {campusArea.sourceCount} source{campusArea.sourceCount === 1 ? "" : "s"}
            </h4>
            <ul className="fab-news__list">
              {campusArea.news.map((n, i) => (
                <li key={`${n.link}-${i}`} className={`fab-news__item${n.here ? " fab-news__item--here" : ""}`}>
                  <a href={n.link} target="_blank" rel="noopener noreferrer" className="fab-news__link">
                    <span className="fab-news__title" lang="th">{n.title}</span>
                  </a>
                  <span className="fab-news__meta mono">
                    {n.publisher && <span className="fab-news__pub">{n.publisher}</span>}
                    {n.publishedAt && <span>{ago(n.publishedAt)}</span>}
                    {n.here ? (
                      <span className="fab-news__here" title="Headline names this area">names this area</span>
                    ) : (
                      <span className="fab-news__prov" title="Headline covers the province, not this area">
                        province-wide
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ── Every other area FloodDash is tracking ──────────────────── */}
        {rest.length > 0 && (
          <div className="fab-others">
            <h4 className="fab-sub eyebrow mono">
              Other areas · {rest.length} · nearest first
            </h4>
            <ul className="fab-others__list">
              {rest.slice(0, 12).map((a) => (
                <li key={a.key} className="fab-row">
                  <TierBadge tier={a.tier} />
                  <AreaName area={a} />
                  <span className="fab-row__dist mono">{a.distanceKm}km</span>
                  {a.score != null && <span className="fab-row__score mono">{a.score}</span>}
                  {a.sourceCount > 0 && (
                    <span className="fab-row__src mono" title={`${a.sourceCount} sources in window`}>
                      {a.sourceCount}×
                    </span>
                  )}
                  {a.latestAt && <span className="fab-row__age mono">{ago(a.latestAt)}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* The method travels with the data, so the weighting is auditable
            rather than taken on trust. */}
        {feed.methodEn && (
          <p className="fab-method" lang="th" title={feed.methodEn}>
            {feed.methodEn}
          </p>
        )}

        <p className="fab-note">
          {fmtAge(ageMinutes)} · {feed.meta.counts.confirmed} confirmed · {feed.meta.counts.reported} reported ·{" "}
          {feed.meta.counts.sensor} sensor-only
        </p>
      </div>
    </section>
  );
}